import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, User, Loader2 } from 'lucide-react';

interface LoginForm {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  console.log('[Login] Rendering login component');
  const { user, login, loading: authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    mode: 'onChange',
  });

  // Redirection si déjà connecté
  useEffect(() => {
    if (user) {
      console.log('[Login] User already authenticated, redirecting');
      navigate(`/${user.role}/dashboard`, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (data: LoginForm) => {
    // Ne jamais journaliser le mot de passe saisi
    console.log('[Login] Form submitted for:', data.email);
    setIsLoading(true);
    try {
      const success = await login(data.email, data.password);
      if (success) {
        console.log('[Login] Login successful');
      }
    } catch (error) {
      console.error('[Login] Error during login:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (user) {
    return <Navigate to={`/${user.role}/dashboard`} replace />;
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* En-tête */}
        <div className="text-center">
          <div className="mx-auto w-32 h-32 rounded-full overflow-hidden bg-white shadow-lg ring-4 ring-brand-600/20">
            <img
              src="/logo.png"
              alt="Logo LMS Kayliss"
              className="w-full h-full object-cover"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            LMS Kayliss
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Plateforme d'apprentissage pour entraîneurs de football
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Champ email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Adresse email
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('email', {
                    required: 'L\'email est requis',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Adresse email invalide'
                    }
                  })}
                  type="email"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  placeholder="votre@email.com"
                  disabled={isLoading || authLoading}
                />
                <User className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-brand-600">{errors.email.message}</p>
              )}
            </div>

            {/* Champ mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password', {
                    required: 'Le mot de passe est requis',
                    minLength: {
                      value: 6,
                      message: 'Le mot de passe doit contenir au moins 6 caractères'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  placeholder="••••••••"
                  disabled={isLoading || authLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading || authLoading}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-brand-600">{errors.password.message}</p>
              )}
            </div>

            {/* Bouton de soumission */}
            <div>
              <button
                type="submit"
                disabled={isLoading || authLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin h-5 w-5" />
                ) : (
                  'Se connecter'
                )}
              </button>
            </div>
          </form>

          {/* Comptes de démonstration */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="text-sm text-gray-600">
              <p className="font-medium mb-2">Comptes de démonstration :</p>
              <div className="space-y-1">
                <p><strong>Formateur:</strong> demo.formateur@kayliss.fr / DemoFormateur2026</p>
                <p><strong>Apprenant:</strong> demo.etudiant@kayliss.fr / DemoEtudiant2026</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;